import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import EmailEditor from "react-email-editor";
import {
  BracesIcon,
  CodeIcon,
  FileArchiveIcon,
  FileTextIcon,
  ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SaveButton } from "@/components/ui/save-button";
import { Spinner } from "@/components/ui/spinner";
import { formatError } from "helpers/formatError";
import { buildUnlayerOptions } from "helpers/unlayerOptions";
import { getUnlayerTheme } from "helpers/unlayerTheme";
import EmailTemplateService from "services/EmailTemplate";
import ExportService from "services/Export";
import EditDesignJsonDialog from "routes/email/components/EditDesignJsonDialog";

const EXPORT_OPTIONS = [
  {
    name: "HTML",
    icon: CodeIcon,
    method: "exportHtml",
    field: "html",
    type: "text/html",
    ext: "html",
  },
  {
    name: "PDF",
    icon: FileTextIcon,
    method: "exportPdf",
    field: "url",
    type: "application/pdf",
    ext: "pdf",
  },
  {
    name: "Image",
    icon: ImageIcon,
    method: "exportImage",
    field: "url",
    type: "image/png",
    ext: "png",
  },
  {
    name: "ZIP",
    icon: FileArchiveIcon,
    method: "exportZip",
    field: "url",
    type: "application/zip",
    ext: "zip",
  },
];

// Unlayer reports every export through a callback.
const callEditor = (editor, method) =>
  new Promise(resolve => editor[method](resolve));

/**
 * Unlayer drag-and-drop editor. Saving regenerates the template HTML from the
 * design, which is why the HTML tab warns before switching here.
 */
const GraphicalEmailTemplate = ({ template, onSaved, onCancel }) => {
  const editorRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [exportingName, setExportingName] = useState(null);
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [jsonDesign, setJsonDesign] = useState(null);

  const isRawHtml = !!template.info?.is_raw_html;

  const { data: mergeTags, isFetched: mergeTagsFetched } = useQuery({
    queryKey: ["email-template-merge-tags"],
    queryFn: () => EmailTemplateService.getTags(),
  });

  const { data: specialLinks, isFetched: specialLinksFetched } = useQuery({
    queryKey: ["email-template-special-links"],
    queryFn: () => EmailTemplateService.getUrls(),
  });

  // EmailEditor re-creates itself whenever its serialized options change, and
  // they embed the current theme, so they are built once per data load; later
  // theme flips go through setAppearance below instead.
  const editorOptions = useMemo(
    () => ({ ...buildUnlayerOptions(), mergeTags, specialLinks }),
    [mergeTags, specialLinks]
  );

  // Unlayer renders in its own iframe, so it does not see the app's
  // data-theme attribute and has to be told when it flips.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      editorRef.current?.editor?.setAppearance({ theme: getUnlayerTheme() });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { design, html } = await callEditor(
        editorRef.current.editor,
        "exportHtml"
      );
      return EmailTemplateService.editEmailTemplate({
        id: template.id,
        html,
        info: { ...template.info, design, is_raw_html: false },
      });
    },
    onSuccess: onSaved,
  });

  const handleReady = unlayer => {
    if (template.info?.design) {
      unlayer.loadDesign(template.info.design);
    }
    setIsEditorReady(true);
  };

  const handleExport = async option => {
    setExportingName(option.name);
    try {
      const data = await callEditor(editorRef.current.editor, option.method);
      await ExportService.downloadFile(data[option.field], {
        name: `${template.name}.${option.ext}`,
        mimetype: option.type,
      });
    } catch {
      toast.error(`Couldn't export the template as ${option.name}`);
    } finally {
      setExportingName(null);
    }
  };

  const openJsonDialog = async () => {
    // The editor's current design, not the saved one, so unsaved edits show.
    setJsonDesign(await callEditor(editorRef.current.editor, "saveDesign"));
    setIsJsonDialogOpen(true);
  };

  const applyJsonDesign = design => {
    editorRef.current.editor.loadDesign(design);
    setIsJsonDialogOpen(false);
    toast.success("Design applied to the editor");
  };

  if (isRawHtml) {
    return (
      <Alert>
        <AlertTitle>This template is maintained as raw HTML</AlertTitle>
        <AlertDescription>
          It has no graphical design to edit. Change its markup in the HTML tab.
        </AlertDescription>
      </Alert>
    );
  }

  if (!mergeTagsFetched || !specialLinksFetched) {
    return (
      <div className="tw:p-8 tw:text-center">
        <Spinner />
      </div>
    );
  }

  const isExporting = exportingName !== null;

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
        {EXPORT_OPTIONS.map(option => {
          const Icon = option.icon;
          return (
            <Button
              key={option.name}
              type="button"
              variant="outline"
              size="sm"
              disabled={!isEditorReady || isExporting}
              onClick={() => handleExport(option)}
            >
              {exportingName === option.name ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <Icon data-icon="inline-start" />
              )}
              Export {option.name}
            </Button>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!isEditorReady}
          onClick={openJsonDialog}
        >
          <BracesIcon data-icon="inline-start" />
          Edit JSON
        </Button>
      </div>

      <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-(--color-border)">
        <EmailEditor
          ref={editorRef}
          minHeight="60vh"
          options={editorOptions}
          onReady={handleReady}
        />
      </div>

      {saveMutation.isError && (
        <Alert variant="destructive">
          <AlertTitle>Action Failed</AlertTitle>
          <AlertDescription>{formatError(saveMutation.error)}</AlertDescription>
        </Alert>
      )}

      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <SaveButton
          type="button"
          saving={saveMutation.isPending}
          disabled={!isEditorReady}
          onClick={() => saveMutation.mutate()}
        >
          Save & Continue
        </SaveButton>
      </div>

      <EditDesignJsonDialog
        open={isJsonDialogOpen}
        onOpenChange={setIsJsonDialogOpen}
        design={jsonDesign}
        onApply={applyJsonDesign}
      />
    </div>
  );
};

export default GraphicalEmailTemplate;
